import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import {
  selectChildCompanies,
  selectIsInHierarchyContext,
  selectActiveChildCompanyName,
  selectOriginalCompanyName,
  selectSwitchCompanyStatus,
  switchToChildCompany,
  switchBackToOriginalCompany,
} from "app/containers/Hierarchy/slice";
import { selectCompanyInfo } from "app/containers/Global/slice";
import { Button } from "app/shadcnComponents/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "app/shadcnComponents/ui/dropdown-menu";
import { Badge } from "app/shadcnComponents/ui/badge";

export default function CompanySwitcher() {
  const dispatch = useAppDispatch();
  const childCompanies = useAppSelector(selectChildCompanies);
  const isInHierarchyContext = useAppSelector(selectIsInHierarchyContext);
  const activeChildCompanyName = useAppSelector(selectActiveChildCompanyName);
  const originalCompanyName = useAppSelector(selectOriginalCompanyName);
  const switchStatus = useAppSelector(selectSwitchCompanyStatus);
  const companyInfo = useAppSelector(selectCompanyInfo);

  if (childCompanies.length === 0 && !isInHierarchyContext) return null;

  const isSwitching = switchStatus === "loading";

  const handleSelect = async (eventKey: string) => {
    if (isSwitching) return;

    if (eventKey === "__home__") {
      await dispatch(switchBackToOriginalCompany());
      window.location.href = "/home";
      return;
    }

    const company = childCompanies.find(
      (c) => String(c.companyAccountId) === eventKey
    );
    if (!company) return;

    try {
      await dispatch(
        switchToChildCompany({
          childCompanyAccountId: company.companyAccountId,
          childCompanyName: company.companyName,
          originalCompanyName: companyInfo?.companyName,
        })
      ).unwrap();
    } catch {
      toast.error(
        `Couldn't switch to ${company.companyName}. Your access may have changed.`,
        { toastId: "hierarchy-switch-failed" }
      );
      return;
    }
    window.location.href = "/home";
  };

  const activeLabel = isInHierarchyContext
    ? (activeChildCompanyName ?? "Child Company")
    : (companyInfo?.companyName ?? "My Company");

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="tertiary"
          disabled={isSwitching}
          className="flex items-center gap-1.5 text-charcoal-100 font-bold text-base hover:text-sapphire-150 focus-visible:ring-0 px-0"
        >
          <FontAwesomeIcon icon="building" />
          <span className="max-w-[140px] overflow-hidden text-ellipsis whitespace-nowrap">
            {activeLabel}
          </span>
          {isInHierarchyContext && (
            <Badge
              variant="outline"
              className="text-sapphire-100 border-sapphire-100 px-1.5 py-0 text-[10px]"
            >
              Viewing
            </Badge>
          )}
          <FontAwesomeIcon icon="chevron-down" className="text-xs" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="min-w-[220px]">
        {isInHierarchyContext ? (
          // Inside a child the only way out is back to the parent; there is nothing else to switch to.
          companyInfo && (
            <DropdownMenuItem
              onSelect={() => handleSelect("__home__")}
              className="flex items-center gap-2 cursor-pointer"
            >
              <FontAwesomeIcon icon="house" className="text-charcoal-60" />
              Return to {originalCompanyName ?? companyInfo.companyName}
            </DropdownMenuItem>
          )
        ) : (
          <>
            <DropdownMenuLabel className="text-xs text-charcoal-60 uppercase tracking-wide font-normal">
              Switch to
            </DropdownMenuLabel>
            {childCompanies.map((company) => (
              <DropdownMenuItem
                key={company.companyAccountId}
                onSelect={() => handleSelect(String(company.companyAccountId))}
                className="flex items-center justify-between cursor-pointer"
              >
                <span>{company.companyName}</span>
                <Badge
                  variant="outline"
                  className="text-xs text-charcoal-50 ml-2"
                >
                  {company.accessLevel === "read" ? "Read only" : "Admin"}
                </Badge>
              </DropdownMenuItem>
            ))}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
